import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { speakText } from '../utils/audioUtils';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import { supabase } from '../supabase';
import '../screens/OppositeWordsScreen.css';

interface OppositeWord {
  id: string;
  word_italian: string;
  word_bengali: string;
  opposite_italian: string;
  opposite_bengali: string;
  category?: string;
}

const OppositeWordsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const [oppositeWords, setOppositeWords] = useState<OppositeWord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const content = {
    bn: {
      title: "বিপরীত শব্দ",
      searchPlaceholder: "ইতালীয় শব্দ খুঁজুন...",
      back: "ফিরে যান",
      noResults: "কোন ফলাফল পাওয়া যায়নি",
      loading: "লোড হচ্ছে..."
    },
    it: {
      title: "Parole Opposte",
      searchPlaceholder: "Cerca parola italiana...",
      back: "Indietro",
      noResults: "Nessun risultato trovato",
      loading: "Caricamento..."
    },
    en: {
      title: "Opposite Words",
      searchPlaceholder: "Search Italian word...",
      back: "Back",
      noResults: "No results found",
      loading: "Loading..."
    }
  };

  useEffect(() => {
    loadOppositeWords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOppositeWords = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading opposite words from database...');
      
      // Get count first to see how many words are in the database
      const { count, error: countError } = await supabase
        .from('opposite_words')
        .select('*', { count: 'exact', head: true });
      
      if (!countError) {
        console.log(`📊 Total words in database: ${count || 0}`);
      }
      
      // Load all words without any limit
      const { data, error } = await supabase
        .from('opposite_words')
        .select('*')
        .order('word_italian', { ascending: true });

      if (error) {
        console.error('❌ Error loading opposite words from database:', error);
        console.error('📋 Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        // Fallback to default words if table doesn't exist or other error
        console.log('⚠️ Falling back to default words');
        const defaultWords = getDefaultOppositeWords();
        console.log(`📝 Using ${defaultWords.length} default words`);
        setOppositeWords(defaultWords);
      } else {
        const wordCount = data?.length || 0;
        console.log(`✅ Loaded ${wordCount} opposite words from database`);
        console.log('📦 Full data response:', data);
        
        if (data && Array.isArray(data) && data.length > 0) {
          console.log('📝 First 3 words:', data.slice(0, 3));
          console.log('📝 Last 3 words:', data.slice(-3));
          console.log('📋 All word Italian texts:', data.map(w => w.word_italian));
          // Use database data
          setOppositeWords(data);
        } else {
          // If database is empty or returned no data, use default words
          console.log(`⚠️ Database returned ${wordCount} words. Using ${wordCount === 0 ? 'default' : 'empty'} words.`);
          const defaultWords = getDefaultOppositeWords();
          console.log(`📝 Using ${defaultWords.length} default words`);
          setOppositeWords(defaultWords);
        }
      }
    } catch (error) {
      console.error('❌ Exception loading opposite words:', error);
      console.log('⚠️ Falling back to default words due to exception');
      const defaultWords = getDefaultOppositeWords();
      console.log(`📝 Using ${defaultWords.length} default words`);
      setOppositeWords(defaultWords);
    } finally {
      setLoading(false);
    }
  };

  // Default opposite words (fallback if database table doesn't exist)
  const getDefaultOppositeWords = (): OppositeWord[] => [
    { id: '1', word_italian: 'grande', word_bengali: 'বড়', opposite_italian: 'piccolo', opposite_bengali: 'ছোট', category: 'adjectives' },
    { id: '2', word_italian: 'alto', word_bengali: 'উঁচু', opposite_italian: 'basso', opposite_bengali: 'নিচু', category: 'adjectives' },
    { id: '3', word_italian: 'buono', word_bengali: 'ভাল', opposite_italian: 'cattivo', opposite_bengali: 'খারাপ', category: 'adjectives' },
    { id: '4', word_italian: 'bello', word_bengali: 'সুন্দর', opposite_italian: 'brutto', opposite_bengali: 'কুৎসিত', category: 'adjectives' },
    { id: '5', word_italian: 'caldo', word_bengali: 'গরম', opposite_italian: 'freddo', opposite_bengali: 'ঠান্ডা', category: 'adjectives' },
    { id: '6', word_italian: 'bianco', word_bengali: 'সাদা', opposite_italian: 'nero', opposite_bengali: 'কালো', category: 'adjectives' },
    { id: '7', word_italian: 'aperto', word_bengali: 'খোলা', opposite_italian: 'chiuso', opposite_bengali: 'বন্ধ', category: 'adjectives' },
    { id: '8', word_italian: 'nuovo', word_bengali: 'নতুন', opposite_italian: 'vecchio', opposite_bengali: 'পুরানো', category: 'adjectives' },
    { id: '9', word_italian: 'facile', word_bengali: 'সহজ', opposite_italian: 'difficile', opposite_bengali: 'কঠিন', category: 'adjectives' },
    { id: '10', word_italian: 'ricco', word_bengali: 'ধনী', opposite_italian: 'povero', opposite_bengali: 'দরিদ্র', category: 'adjectives' },
    { id: '11', word_italian: 'veloce', word_bengali: 'দ্রুত', opposite_italian: 'lento', opposite_bengali: 'ধীর', category: 'adjectives' },
    { id: '12', word_italian: 'giovane', word_bengali: 'তরুণ', opposite_italian: 'vecchio', opposite_bengali: 'বুড়ো', category: 'adjectives' },
    { id: '13', word_italian: 'forte', word_bengali: 'শক্তিশালী', opposite_italian: 'debole', opposite_bengali: 'দুর্বল', category: 'adjectives' },
    { id: '14', word_italian: 'pieno', word_bengali: 'ভরা', opposite_italian: 'vuoto', opposite_bengali: 'খালি', category: 'adjectives' },
    { id: '15', word_italian: 'felice', word_bengali: 'খুশি', opposite_italian: 'triste', opposite_bengali: 'দুঃখী', category: 'adjectives' },
    { id: '16', word_italian: 'pulito', word_bengali: 'পরিষ্কার', opposite_italian: 'sporco', opposite_bengali: 'নোংরা', category: 'adjectives' },
    { id: '17', word_italian: 'leggero', word_bengali: 'হালকা', opposite_italian: 'pesante', opposite_bengali: 'ভারী', category: 'adjectives' },
    { id: '18', word_italian: 'chiaro', word_bengali: 'পরিষ্কার/উজ্জ্বল', opposite_italian: 'scuro', opposite_bengali: 'অন্ধকার/কালো', category: 'adjectives' },
    { id: '19', word_italian: 'calmo', word_bengali: 'শান্ত', opposite_italian: 'agitato', opposite_bengali: 'অস্থির', category: 'adjectives' },
    { id: '20', word_italian: 'umido', word_bengali: 'ভেজা', opposite_italian: 'secco', opposite_bengali: 'শুষ্ক', category: 'adjectives' },
    { id: '21', word_italian: 'dolce', word_bengali: 'মিষ্টি', opposite_italian: 'amaro', opposite_bengali: 'তিক্ত', category: 'adjectives' },
    { id: '22', word_italian: 'largo', word_bengali: 'চওড়া', opposite_italian: 'stretto', opposite_bengali: 'সংকীর্ণ', category: 'adjectives' },
    { id: '23', word_italian: 'vicino', word_bengali: 'কাছাকাছি', opposite_italian: 'lontano', opposite_bengali: 'দূরে', category: 'adjectives' },
    { id: '24', word_italian: 'dentro', word_bengali: 'ভিতরে', opposite_italian: 'fuori', opposite_bengali: 'বাইরে', category: 'adverbs' },
    { id: '25', word_italian: 'sopra', word_bengali: 'উপরে', opposite_italian: 'sotto', opposite_bengali: 'নিচে', category: 'adverbs' },
    { id: '26', word_italian: 'avanti', word_bengali: 'সামনে', opposite_italian: 'indietro', opposite_bengali: 'পিছনে', category: 'adverbs' },
    { id: '27', word_italian: 'destra', word_bengali: 'ডান', opposite_italian: 'sinistra', opposite_bengali: 'বাম', category: 'nouns' },
    { id: '28', word_italian: 'uomo', word_bengali: 'পুরুষ', opposite_italian: 'donna', opposite_bengali: 'নারী', category: 'nouns' },
    { id: '29', word_italian: 'entrata', word_bengali: 'প্রবেশ', opposite_italian: 'uscita', opposite_bengali: 'প্রস্থান', category: 'nouns' },
    { id: '30', word_italian: 'inizio', word_bengali: 'শুরু', opposite_italian: 'fine', opposite_bengali: 'শেষ', category: 'nouns' },
    { id: '31', word_italian: 'gioia', word_bengali: 'আনন্দ', opposite_italian: 'tristezza', opposite_bengali: 'দুঃখ', category: 'nouns' },
    { id: '32', word_italian: 'amore', word_bengali: 'ভালোবাসা', opposite_italian: 'odio', opposite_bengali: 'ঘৃণা', category: 'nouns' },
    { id: '33', word_italian: 'giorno', word_bengali: 'দিন', opposite_italian: 'notte', opposite_bengali: 'রাত', category: 'nouns' },
    { id: '34', word_italian: 'luce', word_bengali: 'আলো', opposite_italian: 'buio', opposite_bengali: 'অন্ধকার', category: 'nouns' },
    { id: '35', word_italian: 'risposta', word_bengali: 'উত্তর', opposite_italian: 'domanda', opposite_bengali: 'প্রশ্ন', category: 'nouns' },
  ];

  const filteredWords = oppositeWords.filter(word => {
    const query = searchQuery.toLowerCase();
    return (
      word.word_italian.toLowerCase().includes(query) ||
      word.word_bengali.includes(query) ||
      word.opposite_italian.toLowerCase().includes(query) ||
      word.opposite_bengali.includes(query)
    );
  });

  const handleWordClick = async (word: OppositeWord, wordToSpeak: 'left' | 'right') => {
    setSelectedWord(word.id);
    
    // Speak only the clicked word (left or right)
    try {
      if (wordToSpeak === 'left') {
        await speakText(word.word_italian, 'answer');
      } else {
        await speakText(word.opposite_italian, 'answer');
      }
    } catch (error) {
      console.error('Error speaking:', error);
    }

    // Remove selection after a short delay
    setTimeout(() => {
      setSelectedWord(null);
    }, 1500);
  };

  return (
    <div className="opposite-words-screen">
      {/* Header */}
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content[currentLanguage].title}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      {/* Search Section */}
      <div className="word-meaning-search">
        <div className="search-input-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder={content[currentLanguage].searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      {/* Words List */}
      <div className="opposite-words-container">
        {loading ? (
          <div className="loading-message">{content[currentLanguage].loading}</div>
        ) : filteredWords.length === 0 ? (
          <div className="no-results">{content[currentLanguage].noResults}</div>
        ) : (
          <div className="opposite-words-list">
            {filteredWords.map((word) => (
              <div
                key={word.id}
                className={`opposite-word-item ${selectedWord === word.id ? 'selected' : ''}`}
              >
                <div className="opposite-word-pair">
                  <div 
                    className="word-section" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWordClick(word, 'left');
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="word-label">{currentLanguage === 'bn' ? 'শব্দ' : currentLanguage === 'it' ? 'Parola' : 'Word'}</div>
                    <div className="word-italian">{word.word_italian}</div>
                    <div className="word-bengali">{word.word_bengali}</div>
                  </div>
                  <div className="opposite-arrow">⇄</div>
                  <div 
                    className="word-section"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWordClick(word, 'right');
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="word-label">{currentLanguage === 'bn' ? 'বিপরীত' : currentLanguage === 'it' ? 'Opposto' : 'Opposite'}</div>
                    <div className="word-italian">{word.opposite_italian}</div>
                    <div className="word-bengali">{word.opposite_bengali}</div>
                  </div>
                </div>
                {word.category && (
                  <div className="word-category">{word.category}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default OppositeWordsScreen;

